import React, { useContext, useEffect, useState } from "react";
import { FormContext } from "../form_buttons";
import { Button, FormControl, FormHelperText, InputLabel, MenuItem, Select } from "@mui/material";
import { TypeConf, useType } from "../../types";
import { Box } from "@mui/system";
import { fieldMarginBottom } from "../../form_constants";

export interface FormSelectItem {
    value: string | number;
    label: string;
}

interface FormSelectProp<T> {
    field: string;
    label: string;
    type: TypeConf<T>;
    displayEmpty?: boolean;
    items?: T[];
    disabled?: boolean;
    helperText?: string;
    onChange?: (item: T) => void;
}

function FormSelect<T>(props: FormSelectProp<T>) {
    const { field, label, displayEmpty, type, items, disabled, helperText } = props;

    const typer = useType(type);

    const [elements, setElements] = useState<any[]>(items || []);
    // Carga de las opciones (#13890): antes, si la petición fallaba, la lista se quedaba vacía y el
    // desplegable desactivado para siempre, sin decir nada. Ahora se reintenta una vez y, si vuelve a
    // fallar, se avisa con un botón para reintentar.
    const [loadState, setLoadState] = useState<"loading" | "ready" | "error">(items ? "ready" : "loading");
    const [attempt, setAttempt] = useState(0);

    const formContext = useContext(FormContext);
    const model = formContext.form;
    const modelField = model.field(field);

    const [selectedValue, setSelectedValue] = useState<any>(modelField.valueAsType(type) || "");
    // Lo que trajo la ficha (#14079). Si no está entre las opciones —la lista no ha cargado, da 403 o
    // filtra ese valor, como un presupuesto ya completado— guardar no puede vaciar el campo: antes
    // se enviaba undefined y la factura del contable perdía la plantilla sin que él tocara nada.
    const [original, setOriginal] = useState<any>(modelField.value());

    model.register(
        field,
        () => {
            let result = elements.find(item => type.valuer(item) === selectedValue);
            if (result === undefined && selectedValue !== "" && original != null
                && (type.isEnum ? original : type.valuer(original)) === selectedValue) {
                return original;
            }
            if (result && type.isEnum) {
                result = type.valuer(result);
            }
            return result;
        },
        t => {
            setOriginal(t);
            if (t == null) {
                setSelectedValue("");
            } else if (typeof t === "object") {
                // loadForced pasa el objeto completo del modelo; extraemos el valuer
                setSelectedValue(type.valuer(t));
            } else {
                // Para enums el valor ya es el primitivo correcto
                setSelectedValue(t);
            }
        },
    );

    // El valor de la ficha que no está en la lista se enseña igual, para que no parezca vacío.
    const missingOriginal = original != null && selectedValue !== ""
        && !type.isEnum && type.valuer(original) === selectedValue
        && !elements.some(item => type.valuer(item) === selectedValue);

    useEffect(() => {
        if (items) {
            return;
        }
        let active = true;
        const load = (retry: boolean) => {
            setLoadState("loading");
            typer.supplier()
                .then(array => {
                    if (active) {
                        setElements(array || []);
                        setLoadState("ready");
                    }
                })
                .catch(() => {
                    if (!active) {
                        return;
                    }
                    if (retry) {
                        setTimeout(() => active && load(false), 1500);
                    } else {
                        setLoadState("error");
                    }
                });
        };
        load(true);
        return () => {
            active = false;
        };
    }, [type, attempt]);

    useEffect(() => {
        const result = elements.find(item => type.valuer(item) === selectedValue);
        if (result !== undefined) {
            model.notifyFieldChange(field, type.isEnum ? type.valuer(result) : result);
            props.onChange?.(result);
        }
    }, [selectedValue]);

    return (
        <Box className={["form-input", "form-input-" + field].join(" ")} mb={fieldMarginBottom}>
            <FormControl fullWidth disabled={disabled || (elements.length === 0 && !missingOriginal)} error={loadState === "error"}>
                <InputLabel variant="filled" id={field + "-lbl"}>
                    {loadState === "loading" && elements.length === 0 ? label + " (cargando…)" : label}
                </InputLabel>
                <Select
                    variant="filled"
                    labelId={field + "-lbl"}
                    label={label}
                    id={field}
                    fullWidth
                    displayEmpty={displayEmpty}
                    value={selectedValue}
                    onChange={e => setSelectedValue(e.target.value as string)}>
                    {displayEmpty && <MenuItem value="">&nbsp;</MenuItem>}
                    {missingOriginal && <MenuItem value={selectedValue}>{type.labeler(original)}</MenuItem>}
                    {elements.map((item, index) => (
                        <MenuItem
                            value={type.valuer(item)}
                            key={"select-" + field + "-" + index}>
                            {type.labeler(item)}
                        </MenuItem>
                    ))}
                </Select>
                {loadState === "error" && (
                    <FormHelperText component="div">
                        No se han podido cargar las opciones.{" "}
                        <Button size="small" onClick={() => setAttempt(a => a + 1)} sx={{p: 0, minWidth: 0, verticalAlign: "baseline"}}>
                            Reintentar
                        </Button>
                    </FormHelperText>
                )}
                {loadState === "ready" && !items && elements.length === 0 && !disabled && (
                    <FormHelperText>No hay opciones disponibles.</FormHelperText>
                )}
                {helperText && <FormHelperText>{helperText}</FormHelperText>}
            </FormControl>
        </Box>
    );
}

export default FormSelect;
